import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Pagination } from '@sorare/core/src/atoms/navigation/Pagination';
import { useBackendPagination } from '@sorare/core/src/contexts/graphql-search';
import { useConversionCredits } from '@sorare/core/src/hooks/useConversionCredits';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';
import {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';

import type { Props as PlayerDrawerProps } from '#src/components/player/PlayerDrawer/index.tsx';
import { PlayerDrawer } from '#src/components/player/PlayerDrawer/index.tsx';
import {
  PlayerMarketDrawer,
  PlayerMarketDrawerVariant,
} from '#src/components/player/PlayerMarketDrawer/index.tsx';
import {
  BackendSearchPlayerTable,
  SearchPlayerTable,
} from '#src/components/searchPlayerTable/SearchPlayerTable/index.tsx';
import { Pagination as PaginationSearchCards } from '#src/searchCards/Pagination/index.tsx';
import type { PlayerResultsProps } from '#src/searchCommonPlayer/AdvancedPlayerSearch/types.ts';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import type { BackendPaginatedSearchPlayerTable_AnyPlayerInterface } from './__generated__/index.graphql.ts';

export const PaginatedSearchPlayerTable = (props: PlayerResultsProps) => {
  const {
    sport,
    watchlistId: watchlistIdProp,
    favoritesWatchlist,
    canManageWatchlist,
  } = props;
  const { statsManager } = useManagePlayerSearchView();
  const [cardOpened, setCardOpened] =
    useState<PlayerDrawerProps['cardOpened']>(null);
  const [marketPlayerSlug, setMarketPlayerSlug] = useState<string | null>(null);

  const { conversionCredits } = useConversionCredits({
    sport,
  });
  const canUseConversionCredit = conversionCredits?.some(cc => !cc.disabled);

  const watchlistId = canManageWatchlist ? watchlistIdProp : undefined;

  return (
    <>
      <SearchPlayerTable
        {...props}
        onStatsClick={playerSlug =>
          setCardOpened({
            cardSlug: '',
            playerSlug,
          })
        }
        onListingsClick={playerSlug => setMarketPlayerSlug(playerSlug)}
        watchlistId={watchlistId}
        favoritesWatchlist={favoritesWatchlist}
      />
      <PaginationSearchCards visible />
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
        withBuyingOptions={{
          rarity: statsManager[PlayerSearchViewAttributes.RARITY],
          inSeason:
            statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
            So5LeaderboardSeasonality.IN_SEASON,
          onMarketClick: slug => setMarketPlayerSlug(slug),
        }}
        watchlistId={watchlistId}
        favoritesWatchlist={favoritesWatchlist}
      />
      <PlayerMarketDrawer
        onClose={() => setMarketPlayerSlug(null)}
        playerSlug={marketPlayerSlug}
        sport={sport}
        canUseConversionCredit={canUseConversionCredit}
        origin={{
          instantBuy: ClickInstantBuy_Flow.SCOUTING_CENTER,
          addToCart: ClickAddToCart_Origin.SCOUTING_CENTER,
        }}
        variant={PlayerMarketDrawerVariant.SCOUTING_CENTER}
      />
    </>
  );
};

export const BackendPaginatedSearchPlayerTable = (
  props: PlayerResultsProps & {
    watchlistId?: string;
    favoritesWatchlist?: boolean;
    items: {
      anyPlayer: BackendPaginatedSearchPlayerTable_AnyPlayerInterface;
    }[];
    loading: boolean;
  }
) => {
  const { refine: refinePage, pages, page } = useBackendPagination();
  const { sport, watchlistId, favoritesWatchlist } = props;
  const { statsManager } = useManagePlayerSearchView();
  const [cardOpened, setCardOpened] =
    useState<PlayerDrawerProps['cardOpened']>(null);
  const [marketPlayerSlug, setMarketPlayerSlug] = useState<string | null>(null);

  const { conversionCredits } = useConversionCredits({
    sport,
  });
  const canUseConversionCredit = conversionCredits?.some(cc => !cc.disabled);

  return (
    <>
      <BackendSearchPlayerTable
        {...props}
        onStatsClick={playerSlug =>
          setCardOpened({
            cardSlug: '',
            playerSlug,
          })
        }
        onListingsClick={playerSlug => setMarketPlayerSlug(playerSlug)}
        watchlistId={watchlistId}
        favoritesWatchlist={favoritesWatchlist}
      />
      <Pagination
        currentPage={page - 1}
        pages={pages}
        onSelect={nextPage => {
          refinePage(nextPage);
        }}
      />
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
        withBuyingOptions={{
          rarity: statsManager[PlayerSearchViewAttributes.RARITY],
          inSeason:
            statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
            So5LeaderboardSeasonality.IN_SEASON,
          onMarketClick: slug => setMarketPlayerSlug(slug),
        }}
        watchlistId={watchlistId}
        favoritesWatchlist={favoritesWatchlist}
      />
      <PlayerMarketDrawer
        onClose={() => setMarketPlayerSlug(null)}
        playerSlug={marketPlayerSlug}
        sport={sport}
        canUseConversionCredit={canUseConversionCredit}
        origin={{
          instantBuy: ClickInstantBuy_Flow.SCOUTING_CENTER,
          addToCart: ClickAddToCart_Origin.SCOUTING_CENTER,
        }}
        variant={PlayerMarketDrawerVariant.SCOUTING_CENTER}
      />
    </>
  );
};

BackendPaginatedSearchPlayerTable.fragments = {
  AnyPlayerInterface: gql`
    fragment BackendPaginatedSearchPlayerTable_AnyPlayerInterface on AnyPlayerInterface {
      slug
      ...BackendSearchPlayerTable_AnyPlayerInterface
    }
    ${BackendSearchPlayerTable.fragments.AnyPlayerInterface}
  ` as TypedDocumentNode<BackendPaginatedSearchPlayerTable_AnyPlayerInterface>,
};
